import { Component, OnInit } from '@angular/core';
import { AddRestaurantComponent } from '../../components/modals/add-restaurant/add-restaurant.component';
import { AdminService } from '../../services/admin/admin.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Chain } from '../../interfaces/chain';
import { MatDialog } from '@angular/material/dialog';
import { NotifierService } from 'angular-notifier';
import { TranslateService } from '@ngx-translate/core';
import { IRestaurantInfo } from '../../interfaces/restaurant';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';
import { PageEvent } from '@angular/material/paginator';

@UntilDestroy()
@Component({
  selector: 'app-super-admin',
  templateUrl: './restaurants-page.component.html',
  styleUrls: ['./restaurants-page.component.scss']
})
export class RestaurantsPageComponent implements OnInit{

  public allRestaurants!: IRestaurantInfo[];
  public chainId!: number;
  public currentChain!: Chain;
  public restaurants: IRestaurantInfo[] = [];
  public isEdited = false;
  public searchTerm$ = new BehaviorSubject<string>('');

  public pageSize = 10;
  public pageSizeOptions = [10, 25, 50, 100];
  public pageIndex = 0;

  private lastRestaurantId: string | null = null;
  public editable = false;
  public loading$ = new BehaviorSubject<boolean>(true);

  constructor(
    private activateRoute: ActivatedRoute,
    private matDialog: MatDialog,
    private adminService: AdminService,
    private notifier: NotifierService,
    private translate: TranslateService,
    private router: Router,
    public auth: AuthService
  ) { }

  ngOnInit(): void {
    this.getCurrentChain();

    this.activateRoute.queryParams.pipe(
      filter(params => params.s !== undefined),
      tap(params => {
        const searchParam = params.s || '';
        this.searchTerm$.next(searchParam);
      }),
      untilDestroyed(this)
    ).subscribe();

    this.searchTerm$.pipe(
      tap((searchTerm) => this.adminService.searchTerm$.next(searchTerm)),
      untilDestroyed(this)
    ).subscribe();

    this.adminService.filteredRestaurants$.pipe(
      tap(() => this.loading$.next(true)),
      untilDestroyed(this)
    ).subscribe((restaurants) => {
      this.restaurants = restaurants;
      const itemPerPage = sessionStorage.getItem('chain.' + this.currentChain.id.toString() + '.restaurants.pageSize');
      this.pageSize = itemPerPage ? parseInt(itemPerPage, 10) : 10;
      const index = this.restaurants.findIndex(restaurant => restaurant.id.toString() === this.lastRestaurantId);
      this.pageIndex = index !== -1 ? Math.floor(index / this.pageSize) : 0;
      this.loading$.next(false);
    });

    this.adminService.filteredRestaurants$.pipe(
      switchMap(() => this.activateRoute.fragment),
      filter(fragment => !!fragment),
      tap(fragment => {
        this.lastRestaurantId = fragment;
        if (this.lastRestaurantId) {
          this.scrollToElement(this.lastRestaurantId as string);
          this.router.navigate([], {
            fragment: undefined,
            queryParamsHandling: 'preserve',
            replaceUrl: true
          });
        }
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  private getCurrentChain(): void {
    this.activateRoute.parent?.data.pipe(
      map(data => data.chain),
      tap(chain => {
        this.editable = this.auth.isSuperAdmin() || this.auth.isAdmin(chain);
        this.currentChain = chain;
      }),
      switchMap(chain => this.adminService.getRestaurantsByChainId(chain.id)),
      untilDestroyed(this),
    ).subscribe(() =>  this.loading$.next(false));
  }

  private scrollToElement(fragment: string): void {
    setTimeout(() => {
      const element = document.getElementById(fragment);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        element.classList.add('highlight');
        setTimeout(() => {
          element.classList.remove('highlight');
          this.lastRestaurantId = null;
        }, 2000);
      }

    }, 500);
  }

  get isEmpty(): boolean {
    return this.adminService.restaurants$.value.length === 0;
  }

  public openAddRestaurant(): void {
    const restaurantModal = this.matDialog.open(AddRestaurantComponent, {
      data: {
        chainId: this.currentChain.id
      }
    });

    restaurantModal.afterClosed().pipe(
      filter(Boolean)
    ).subscribe((data) => {
      this.notifier.notify('success', this.translate.instant('pages.restaurants.addRestaurantSuccessfully'));
      this.adminService.restaurants$.next([data as IRestaurantInfo, ...this.adminService.restaurants$.value]);
    });
  }

  public searchRestaurants(searchTerm: string): void {
    this.pageIndex = 0;
    this.searchTerm$.next(searchTerm);
    sessionStorage.setItem('chain.' + this.currentChain.id + '.restaurants', searchTerm);
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    sessionStorage.setItem('chain.' + this.currentChain.id + '.restaurants.pageSize', event.pageSize.toString());
    document.body.scrollIntoView({ behavior: 'auto' });
  }

}
